<template>
  <Form
    :label-col="labelCol"
    :wrapper-col="wrapperCol"
    :validate-trigger="['blur', 'change']"
    :autocomplete="autocomplete ?? 'off'"
  >
    <Row>
      <template v-for="i in keys">
        <Col
          v-if="!configData[i].hidden"
          :key="i"
          :span="configData[i].span || 24"
        >
          <Form.Item
            :label="configData[i].label"
            :extra="configData[i].extra || ''"
            v-bind="validateInfos[i]"
          >
            <div v-if="configData[i].antType === 'text'">
              {{ FormData[i] || "--" }}
            </div>
            <Input
              v-if="configData[i].antType === 'input'"
              v-model:value="FormData[i]"
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              :maxlength="100"
              v-bind="configData[i].bind"
            />
            <Input.TextArea
              v-if="configData[i].antType === 'textArea'"
              v-model:value="FormData[i]"
              placeholder="不超过300字"
              show-count
              :maxlength="300"
              v-bind="configData[i].textAreaBind"
            />
            <Input.Password
              v-if="configData[i].antType === 'inputPassword'"
              v-model:value="FormData[i]"
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              v-bind="configData[i].bind"
            />
            <Select
              v-if="configData[i].antType === 'select'"
              v-model:value="FormData[i]"
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              allow-clear
              v-bind="configData[i].bind"
            >
              <template #option="option">
                <span class="custom-option">
                  {{
                    configData[i].bind?.fieldNames?.label
                      ? option[configData[i].bind?.fieldNames?.label || "label"]
                      : option["label"]
                  }}
                  <span v-if="option.tips" class="custom-option__tips ml-3">{{
                    option.tips
                  }}</span>
                </span>
              </template>
            </Select>
            <DatePicker
              v-if="configData[i].antType === 'datePicker'"
              v-model:value="FormData[i]"
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              v-bind="configData[i].datePickerBind"
              class="w100"
            />
            <Upload
              v-if="configData[i].antType === 'upload'"
              v-model:file-list="FormData[i]"
              v-bind="configData[i].uploadBind"
            />
            <UploadImg
              v-if="configData[i].antType === 'uploadImg'"
              v-model:file-list="FormData[i]"
            />
            <Cascader
              v-if="configData[i].antType === 'cascader'"
              v-model:value="FormData[i]"
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              v-bind="configData[i].cascaderBind"
            />
            <TreeSelect
              v-if="configData[i].antType === 'treeSelect'"
              v-model:value="FormData[i]"
              allow-clear
              tree-default-expand-all
              :placeholder="`${getPrefix(configData[i].antType)}${
                configData[i].label
              }`"
              v-bind="configData[i].treeSelectBind"
            />
            <GroupCom
              v-if="configData[i].antType === 'radioGroup'"
              v-model:value="FormData[i]"
              v-bind="configData[i].radioGroupBind"
            />
          </Form.Item>
        </Col>
      </template>
    </Row>
  </Form>
</template>
<script lang="ts" setup>
import type { StringKeyObject } from "@/types/global";

import type {
  ConfigFormType,
  ExposeType,
  AntType,
  Rule,
} from "@/components/form";

import type { FormProps } from "ant-design-vue";

import { Form, Row, Col, Input } from "ant-design-vue";

import {
  ref,
  toRefs,
  defineProps,
  defineExpose,
  unref,
  watch,
  defineAsyncComponent,
} from "vue";

import { cloneDeep } from "lodash-es";

const Select = defineAsyncComponent(() => import("ant-design-vue/es/select"));

const DatePicker = defineAsyncComponent(
  () => import("ant-design-vue/es/date-picker")
);

const Cascader = defineAsyncComponent(
  () => import("ant-design-vue/es/cascader")
);

const TreeSelect = defineAsyncComponent(
  () => import("ant-design-vue/es/tree-select")
);

const Radio = defineAsyncComponent(() => import("ant-design-vue/es/radio"));

const UploadImg = defineAsyncComponent(
  () => import("@/components/uploadImg/index.vue")
);

const Upload = defineAsyncComponent(
  () => import("@/components/upload/index.vue")
);

interface RulesType {
  [key: string]: Rule[];
}

interface Props {
  configData: ConfigFormType;
  configLayout?: {
    labelCol?: FormProps["labelCol"];
    wrapperCol?: FormProps["wrapperCol"];
  };
  /** 表单 autocomplete，默认 'off'。授权密码等需浏览器保存/填充时传 'on' */
  autocomplete?: string;
}

const GroupCom = Radio.Group;

const prop = defineProps<Props>();

const { configData, configLayout } = toRefs(prop);

const getPrefix = (type: AntType): string => {
  let prefix = "";
  switch (type) {
    case "input":
      prefix = "请填写";
      break;
    case "inputPassword":
      prefix = "请输入";
      break;
    case "select":
      prefix = "请选择";
      break;
    case "datePicker":
      prefix = "请选择";
      break;
    case "textArea":
      prefix = "请填写";
      break;
    case "cascader":
      prefix = "请选择";
      break;
    case "treeSelect":
      prefix = "请选择";
      break;
    case "uploadImg":
      prefix = "请上传";
      break;
    case "upload":
      prefix = "请上传";
      break;
    case "radioGroup":
      prefix = "请选择";
      break;
  }
  return prefix;
};

const getFormData = (): StringKeyObject => {
  const keys = Object.keys(configData.value);
  const data: StringKeyObject = {};
  keys.forEach((i) => {
    data[i] = configData.value[i].value;
  });
  return data;
};

const getRules = (): RulesType => {
  const keys = Object.keys(configData.value);
  const data: RulesType = {};
  keys.forEach((i) => {
    if (configData.value[i]?.hidden) {
      return;
    }
    const rules = configData.value[i].rules;
    if (Array.isArray(rules) && rules.length) {
      // 自动生成message
      if (!rules[0].message && !rules[0].validator) {
        const text = `${getPrefix(configData.value[i].antType)}${
          configData.value[i].label
        }`;
        rules[0].message = text;
      }
      data[i] = rules;
    }
  });
  return data;
};

const labelCol = configLayout?.value?.labelCol || {};

const wrapperCol = configLayout?.value?.wrapperCol || {};

const FormData = ref(getFormData());

const rulesRef = ref(getRules());

const keys = ref(Object.keys(configData.value));

const { validateInfos, validate: validateForm } = Form.useForm(
  FormData,
  rulesRef
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validate: <T = any>() => Promise<T> = () => {
  return new Promise((resolve, reject) => {
    validateForm()
      .then((res) => resolve(cloneDeep(unref(FormData.value) as typeof res)))
      .catch((e) => {
        reject(e);
      });
  });
};

watch(
  () => FormData.value,
  () => {
    keys.value.forEach((i) => {
      configData.value[i].value = FormData.value[i];
    });
  },
  { deep: true }
);

watch(
  () => configData.value,
  () => {
    FormData.value = getFormData();
    rulesRef.value = getRules();
  },
  { deep: true }
);

defineExpose<ExposeType>({
  validate,
});
</script>
<style lang="less" scoped>
.custom-option {
  display: flex;
  align-items: center;
  &__tips {
    color: rgba(0, 0, 0, 0.25);
  }
}
:deep(.ant-form-item-label) {
  label {
    color: #676767;
  }
}
</style>
